import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo, useCallback, useContext, useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { useSelector } from 'react-redux';
import { useQuery } from 'data-fetching-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import * as RequestStates from 'ecosystem-marketplace-core/constants/RequestState';
import { formatCentsToDollars } from 'marketplace-ui-common/utils/currency';
import { isFulfilled, isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { useAssetInstallability } from 'marketplace-ui-assets-core/hooks/useAssetInstallability';
import * as AssetUninstallableReason from '../../constants/AssetUninstallableReason';
import { getAppContextCoreType } from '../../selectors/app';
import { GET_ASSET_CONTENTS } from '../../details/queries/assetContentsQuery';
import { selectPortalScopes, selectUserScopes, getIsUngatedForTemplateTypes // @ts-expect-error untyped dependency
} from '../../selectors/auth';
import { useModuleCategories } from '../hooks/useModuleCategories';
import AssetDetailsPage from '../../details/components/AssetDetailsPage';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import ExpandableTagsSection from '../../details/components/ExpandableTagsSection';
import ModuleContentsResponse from '../records/ModuleContentsResponse';
import ModuleRecommendations from './ModuleRecommendationsSection';
export default function ModuleDetailsPage({
  moduleDetails,
  use
}) {
  const isAdminPreview = use === 'admin-preview';
  const isPreview = use === 'preview' || isAdminPreview;
  const appContext = useSelector(getAppContextCoreType);
  const userScopes = useSelector(selectUserScopes);
  const portalScopes = useSelector(selectPortalScopes);
  const {
    offering: {
      homemade,
      slug
    },
    offeringId,
    pricing,
    provider: {
      name: providerName
    },
    s3BucketKey,
    tags
  } = moduleDetails;
  const {
    assetUninstallableReason,
    requestState: assetInstallabilityRequestState
  } = useAssetInstallability({
    appContext,
    offeringId,
    portalScopes,
    userScopes
  });
  const {
    planType,
    cents
  } = pricing;
  const isFree = planType === PlanType.FREE;
  const getEventProperties = useCallback(() => {
    return {
      screen: 'details',
      slug,
      type: 'module',
      homemade,
      offeringId,
      orderTotal: isFree ? 0 : formatCentsToDollars(cents),
      isInstallableByUser: assetUninstallableReason !== AssetUninstallableReason.MISSING_MARKETPLACE_ASSET_ACCESS,
      isNotInstallableReason: assetUninstallableReason || undefined
    };
  }, [assetUninstallableReason, cents, homemade, isFree, offeringId, slug]);
  /**
   * Usage tracking
   */

  const {
    setDefaultEventProperties,
    trackInteraction,
    trackPageview
  } = useContext(StorefrontUsageTrackerContext); // Set default event properties

  useEffect(() => {
    setDefaultEventProperties(getEventProperties());
  }, [getEventProperties, setDefaultEventProperties]); // Reset default event properties on unmount

  useEffect(() => () => setDefaultEventProperties(null), [setDefaultEventProperties]);
  const {
    loading,
    error,
    data,
    previousData
  } = useQuery(GET_ASSET_CONTENTS, {
    variables: {
      bucketKey: s3BucketKey || ''
    }
  });
  const moduleContentsRequestState = useMemo(() => {
    if (loading) {
      return RequestStates.PENDING;
    } else if (error) {
      return RequestStates.FAILED;
    } else if (data || previousData) {
      return RequestStates.SUCCEEDED;
    } else {
      return RequestStates.UNINITIALIZED;
    }
  }, [loading, data, previousData, error]);
  const moduleContentsData = useMemo(() => {
    const contentsData = data || previousData;

    if (!contentsData) {
      return new ModuleContentsResponse();
    } else {
      return new ModuleContentsResponse(contentsData.contents);
    }
  }, [data, previousData]);
  const hostTemplateTypes = useMemo(() => {
    return moduleContentsData.contents.reduce((final, contentItem) => {
      return [...final, ...contentItem.hostTemplateTypes];
    }, []);
  }, [moduleContentsData]);
  const categories = useMemo(() => {
    return moduleContentsData.contents.reduce((final, contentItem) => {
      return [...final, ...contentItem.categories];
    }, []).map(category => category.toUpperCase()); // TODO: work with CPG to normalize categories
  }, [moduleContentsData]);
  const {
    requestState: moduleCategoriesRequestState,
    getCategoryDisplayName
  } = useModuleCategories();
  const categoryTranslations = useMemo(() => categories.map(category => getCategoryDisplayName(category)), [categories, getCategoryDisplayName]);

  const getModuleHostTemplateTypesI18nKey = hostTemplateType => `marketplace-assets-ui.modules.hostTemplateTypes.${hostTemplateType}`;

  const isUngatedForTemplateTypes = useSelector(getIsUngatedForTemplateTypes);
  const renderedExpandableTagsSection = useMemo(() => /*#__PURE__*/_jsxs(Fragment, {
    children: [isUngatedForTemplateTypes && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx("dt", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.modules.details.moduleInfo.hostTemplateTypes"
        })
      }), /*#__PURE__*/_jsx("dd", {
        children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
          isPreview: isPreview,
          onTrackInteraction: trackInteraction,
          queryField: "templateTypes",
          tags: hostTemplateTypes,
          getTagI18nKey: getModuleHostTemplateTypesI18nKey
        })
      })]
    }), categories.length > 0 && isSucceeded(moduleCategoriesRequestState) && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx("dt", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.modules.details.moduleInfo.categories"
        })
      }), /*#__PURE__*/_jsx("dd", {
        children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
          getTagText: category => getCategoryDisplayName(category),
          isPreview: isPreview,
          onTrackInteraction: trackInteraction,
          queryField: "categories",
          tags: categories
        })
      })]
    }), tags.length > 0 && /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx("dt", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.modules.details.moduleInfo.searchTags"
        })
      }), /*#__PURE__*/_jsx("dd", {
        children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
          isPreview: isPreview,
          onTrackInteraction: trackInteraction,
          queryField: "tags",
          tags: tags
        })
      })]
    })]
  }), [categories, getCategoryDisplayName, hostTemplateTypes, isUngatedForTemplateTypes, moduleCategoriesRequestState, isPreview, tags, trackInteraction]);
  const renderedAssetRecommendationsSection = useMemo(() => /*#__PURE__*/_jsx(ModuleRecommendations, {
    categories: categories,
    categoryTranslations: categoryTranslations,
    isPreview: isPreview,
    offeringId: offeringId,
    onTrackInteraction: trackInteraction,
    onTrackPageview: trackPageview,
    providerName: providerName
  }), [categories, categoryTranslations, isPreview, offeringId, providerName, trackInteraction, trackPageview]);
  useEffect(() => {
    if (isFulfilled(assetInstallabilityRequestState)) {
      trackPageview();
    }
  }, [assetInstallabilityRequestState, moduleContentsRequestState, trackPageview]);
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "MODULE_DETAILS_LOAD",
    children: /*#__PURE__*/_jsx(AssetDetailsPage, {
      assetContentsRequestState: moduleContentsRequestState,
      assetDetails: moduleDetails,
      assetScopes: moduleContentsData.scopes,
      offeringType: "MODULE",
      renderedAssetRecommendationsSection: renderedAssetRecommendationsSection,
      renderedExpandableTagsSection: renderedExpandableTagsSection,
      use: use
    })
  });
}